import React, { useState, useEffect } from 'react';
import { MdContentCopy, MdCheckCircle, MdArrowBack, MdKeyboardArrowDown } from 'react-icons/md';
import { useNavigate, useLocation } from 'react-router-dom'; 
import Swal from 'sweetalert2';
import './Deposit.css';
import { db } from "../firebase"; 
import { ref, push, onValue } from "firebase/database";

// High-Quality Asset Logos for the UI
const ASSET_LOGOS = {
  USDT: "https://cryptologos.cc/logos/tether-usdt-logo.png",
  TRX: "https://cryptologos.cc/logos/tron-trx-logo.png",
  ETH: "https://cryptologos.cc/logos/ethereum-eth-logo.png",
  BTC: "https://cryptologos.cc/logos/bitcoin-btc-logo.png",
  USDC: "https://cryptologos.cc/logos/usd-coin-usdc-logo.png"
};

const Deposit = () => {
  const navigate = useNavigate();
  const location = useLocation(); 
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState('');
  const [copied, setCopied] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('TRX');
  const [allPaymentMethods, setAllPaymentMethods] = useState({});
  
  const [paymentSettings, setPaymentSettings] = useState({
    address: "Loading...", 
    qrUrl: "" 
  });

  // Fetching real-time data from Firebase adminSettings/payments
  useEffect(() => {
    const adminPayRef = ref(db, 'adminSettings/payments');
    const unsub = onValue(adminPayRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setAllPaymentMethods(data);
        const current = data[selectedCurrency];
        if (current) {
          setPaymentSettings({
            address: current.address || 'Not Configured',
            qrUrl: current.qrUrl || ''
          });
        } else {
          setPaymentSettings({
            address: 'Address Not Found',
            qrUrl: ''
          });
        }
      }
    }, (error) => {
      console.error("Firebase Fetch Error:", error);
    });

    return () => unsub();
  }, [selectedCurrency]);

  const handleNextStep = async () => {
    if (!amount || parseFloat(amount) < 10) {
      return Swal.fire({
        icon: 'error',
        title: 'Invalid Amount',
        text: 'Minimum deposit is 10 USDT equivalent',
        confirmButtonColor: '#f3ba2f'
      });
    }

    if (paymentSettings.address === "Loading..." || paymentSettings.address === "Not Configured") {
      return Swal.fire({
        icon: 'warning',
        title: 'Gateway Error',
        text: 'Payment gateway for this asset is not configured yet.',
        confirmButtonColor: '#f3ba2f'
      });
    }

    const savedUser = JSON.parse(localStorage.getItem('user'));
    if (!savedUser || !savedUser.username) {
      return Swal.fire({ icon: 'error', title: 'Session Expired', text: 'Please login again.' });
    }

    // Current Timestamp for instant history display
    const currentTimestamp = Date.now();

    // Step 1: Deposit Request Data (Admin Review ke liye)
    const depositData = {
      username: savedUser.username,
      amount: parseFloat(amount),
      walletAddress: paymentSettings.address,
      currency: selectedCurrency,
      network: selectedCurrency === 'ETH' || selectedCurrency === 'USDC' ? "ERC20" : "TRC20",
      status: "pending",
      timestamp: currentTimestamp,
    };

    // Step 2: Transaction History Entry (User Profile/History ke liye)
    const transactionEntry = {
      username: savedUser.username,
      type: "Deposit",
      amount: parseFloat(amount),
      currency: selectedCurrency,
      status: "Pending", // Direct pending history mein show hoga
      timestamp: currentTimestamp,
      details: `Deposit via ${selectedCurrency}`
    };

    try {
      // Dono records ko parallel save kar rahe hain
      await push(ref(db, 'depositRequests'), depositData);
      await push(ref(db, 'transactions'), transactionEntry); 
      
      setStep(2);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Connection Error', text: error.message });
    }
  };

  const handleCopy = () => {
    if (paymentSettings.address) {
      navigator.clipboard.writeText(paymentSettings.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleBackNavigation = () => {
    if (step === 2) {
      setStep(1);
    } else {
      if (location.state && location.state.activeTab === 'mine') {
        navigate('/', { state: { activeTab: 'mine' } });
      } else {
        navigate('/');
      }
    }
  };

  const handleFinalSubmit = () => {
    Swal.fire({
      icon: 'success',
      title: 'Request Submitted',
      text: 'Your deposit is under review. Please wait for confirmation.',
      confirmButtonColor: '#f3ba2f'
    }).then(() => {
        if (location.state && location.state.activeTab === 'mine') {
            navigate('/', { state: { activeTab: 'mine' } });
          } else {
            navigate('/');
          }
    });
  };

  return (
    <div className="deposit-wrapper-premium">
      <div className="deposit-nav">
        <MdArrowBack onClick={handleBackNavigation} className="nav-icon" />
        <span>Deposit {selectedCurrency} Gateway</span>
      </div>

      <div className="deposit-content">
        {step === 1 ? (
          <div className="luxury-card animated-fade">
            <div className="card-header-premium">
              <div className="step-indicator">Step 01</div>
              <h3>Select Asset & Amount</h3>
              <p>Choose your preferred payment method from below</p>
            </div>

            <div className="premium-dropdown-container">
              <label>Select Asset</label>
              <div className="custom-select-wrapper">
                <img src={ASSET_LOGOS[selectedCurrency]} alt="logo" className="select-asset-icon" />
                <select 
                  value={selectedCurrency} 
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                  className="premium-select"
                >
                  <option value="TRX">TRX (TRON)</option>
                  <option value="ETH">ETH (Ethereum)</option>
                  <option value="BTC">BTC (Bitcoin)</option>
                  <option value="USDC">USDC (ERC20)</option>
                </select>
                <MdKeyboardArrowDown className="select-arrow" />
              </div>
            </div>
            
            <div className="premium-input-container">
              <label>Amount to Deposit</label>
              <div className="input-with-label">
                <input 
                  type="number" 
                  placeholder="0.00" 
                  value={amount} 
                  onChange={(e) => setAmount(e.target.value)}
                />
                <div className="currency-badge-gold">USDT</div>
              </div>
            </div>

            <div className="quick-grid-luxury">
              {[50, 100, 200, 500, 1000, 2000].map(val => (
                <button key={val} onClick={() => setAmount(val.toString())} className="luxury-amt-btn">
                  ${val}
                </button>
              ))}
            </div>

            <button className="main-gold-btn" onClick={handleNextStep}>
              PROCEED TO PAYMENT
            </button>
            <p className="min-deposit-notice">Minimum allowed deposit: <span>10.00 USDT</span></p>
          </div>
        ) : (
          <div className="luxury-card animated-fade">
            <div className="payment-asset-header">
               <img src={ASSET_LOGOS[selectedCurrency]} alt="asset" className="payment-large-logo" />
               <h3>Transfer {selectedCurrency}</h3>
               <p>Pay exactly <span className="gold-text">${parseFloat(amount).toFixed(2)}</span> USDT equivalent</p>
            </div>

            <div className="qr-container-premium">
              <div className="qr-frame">
                <img 
                  src={paymentSettings.qrUrl || `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${paymentSettings.address}`} 
                  alt="QR Code" 
                />
              </div>
            </div>

            <div className="wallet-copy-section">
              <div className="network-info-label">NETWORK: <b>{selectedCurrency === 'TRX' ? 'TRC20' : 'ERC20'}</b></div>
              <div className="copy-field-premium">
                <div className="address-text">{paymentSettings.address}</div>
                <button onClick={handleCopy} className="copy-action-btn">
                  {copied ? <MdCheckCircle color="#0ecb81" size={22} /> : <MdContentCopy size={22} />}
                </button>
              </div>
            </div>

            <div className="luxury-notices">
              <div className="notice-row"><span className="gold-dot"></span><p>Transfer only {selectedCurrency} via compatible network.</p></div>
              <div className="notice-row"><span className="gold-dot"></span><p>Funds will be credited after network confirmation.</p></div>
              <div className="notice-row"><span className="gold-dot"></span><p>Arrival time: usually 1-5 minutes.</p></div>
            </div>

            <button className="main-gold-btn pulse-effect" onClick={handleFinalSubmit}>
              I HAVE PAID
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Deposit;